import { fetchMyProfile, type BackendUserRole } from "@/lib/authApi";

export type AppRole = BackendUserRole;

export interface AuthSessionState {
  loggedIn: boolean;
  user: string;
  role: AppRole;
  token?: string;
  isPreview?: boolean;
  actualUser?: string;
  actualRole?: AppRole;
}

const AUTH_KEY = "elra_auth";
const ROLE_KEY = "elra_role";
const USER_KEY = "elra_user";
const AUTH_TOKEN_KEY = "elra_auth_token";
const AUTH_CHANGE_EVENT = "elra-auth-changed";

const previewableRoles: AppRole[] = ["user", "lessee", "lessor", "agent", "provider", "oem"];

const toAppRole = (value: string | null | undefined): AppRole => {
  const normalized = String(value || "user").trim().toLowerCase();
  return (["lessee", "lessor", "agent", "provider", "oem", "admin", "accountant", "user"] as const).includes(normalized as AppRole)
    ? normalized as AppRole
    : "user";
};

const dispatchAuthChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
};

const resolvePreview = (auth: AuthSessionState): AuthSessionState => {
  if (typeof window === "undefined") return auth;

  const params = new URLSearchParams(window.location.search);
  const previewRole = params.get("previewRole");
  const previewUser = params.get("previewUser");

  if (auth.role === "admin" && previewRole && previewableRoles.includes(previewRole.toLowerCase() as AppRole)) {
    return {
      ...auth,
      user: previewUser?.trim() || auth.user,
      role: previewRole.toLowerCase() as AppRole,
      isPreview: true,
      actualUser: auth.user,
      actualRole: "admin",
    };
  }

  return auth;
};

export const getStoredAuthSession = (): AuthSessionState => {
  if (typeof window === "undefined") {
    return { loggedIn: false, user: "", role: "user" };
  }

  const loggedIn = window.localStorage.getItem(AUTH_KEY) === "true";
  const user = window.localStorage.getItem(USER_KEY) || "";
  const role = toAppRole(window.localStorage.getItem(ROLE_KEY));
  const token = window.localStorage.getItem(AUTH_TOKEN_KEY) || undefined;

  return resolvePreview({
    loggedIn,
    user,
    role,
    token,
  });
};

export const setAuthSession = (session: AuthSessionState) => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(AUTH_KEY, String(Boolean(session.loggedIn)));
  window.localStorage.setItem(ROLE_KEY, session.role);
  window.localStorage.setItem(USER_KEY, session.user);

  if (session.token) {
    window.localStorage.setItem(AUTH_TOKEN_KEY, session.token);
  } else {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
  }

  dispatchAuthChanged();
};

export const persistAuth = setAuthSession;

export const clearAuthSession = () => {
  if (typeof window === "undefined") return;

  window.localStorage.removeItem(AUTH_KEY);
  window.localStorage.removeItem(ROLE_KEY);
  window.localStorage.removeItem(USER_KEY);
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  dispatchAuthChanged();
};

export const logoutUser = () => {
  clearAuthSession();
};

export const getAuthState = () => getStoredAuthSession();
export const isLoggedIn = () => getStoredAuthSession().loggedIn;
export const isAdmin = () => getStoredAuthSession().role === "admin";

export const hydrateAuthSessionFromApi = async (): Promise<AuthSessionState> => {
  const stored = getStoredAuthSession();
  if (!stored.loggedIn || !stored.token) return stored;

  try {
    const profile = await fetchMyProfile(stored.token);
    const user = profile.user?.email || stored.user;
    const role = toAppRole(profile.user?.role || stored.actualRole || stored.role);

    setAuthSession({
      loggedIn: true,
      user,
      role,
      token: stored.token,
    });

    return getStoredAuthSession();
  } catch {
    clearAuthSession();
    return { loggedIn: false, user: "", role: "user" };
  }
};

export const authSessionEventName = AUTH_CHANGE_EVENT;
